import EditorActions from '../actions/editor';
import { closeFlow } from '../actions/reduxActions/flows';
import store from '../store/store';
import alt from '../alt';

import { navigate } from '@reach/router';
import { generateRouteUrl, generateRandomString } from '../utils/core';
import { ALWAYS_OPEN_NEW_TAB } from '../constants';
import { tabDetailsObjects } from '../config/admin';

const homeKey = generateRandomString(16);

export class EditorStore {

    constructor() {

        this.bindListeners({
            openFlow: EditorActions.openFlow,
            openPageLayout: EditorActions.openPageLayout,
            openEditor: EditorActions.openEditor,
            newPageLayout: EditorActions.newPageLayout,
            setEditorPageId: EditorActions.setEditorPageId,
            setEditorName: EditorActions.setEditorNameAndType,
            close: EditorActions.close,
            closeAll: EditorActions.closeAll,
            closePage: EditorActions.closePage,
            openTenantSelector: EditorActions.openTenantSelector,
            closeTenantSelector: EditorActions.closeTenantSelector,
        });

        this.editors = [{ type: 'flows', id: 0, isActive: true, title: 'Flows', key: homeKey }];
        this.isTenantSelectorOpen = false;
    }

    /**
     * @param {Array} editors
     * @description Returns an ID for a new editor based on the highest existing
     * editor ID incremented by 1. This is a workaround to fix the fact that editor ID's
     * referenced in the model do not stay synchronized with the ID's in component state.
     */
    createNewEditorId() {
        const ids = this.editors.map(element => element.id);
        return Math.max(...ids) + 1;
    }

    openFlow(flow) {
        const existingFlowEditor = this.editors.find(editor => editor.type === 'flow' && editor.flowId === flow.id);

        if (existingFlowEditor) {
            const { id, key } = existingFlowEditor;
            this.setActiveEditor({ id, key });
            if (flow.name) {
                // If we're given a name make sure the editor name matches
                existingFlowEditor.title = flow.name;
            }

        } else {
            const id = this.createNewEditorId(this.editors);
            const key = generateRandomString(16);

            this.editors.push({
                title: flow.name,
                type: 'flow',
                icon: 'leaf',
                flowId: flow.id,
                id,
                key,
            });

            this.editors.map((editor, index) => {
                editor.index = index;
                return index;
            });

            this.setActiveEditor({ id, key });
        }
    }

    openPageLayout(page, isNew = false) {
        const openPage = page.key ?
            this.editors.filter(editor => editor.type === 'new_page' && editor.key === page.key)[0] : 
            this.editors.filter(editor => editor.type === 'page' && editor.pageId === page.id)[0];

        if (openPage) {
            const { id, key } = openPage;
            this.setActiveEditor({ id, key });
        } else {
            const id = this.createNewEditorId(this.editors);
            const key = page.key || generateRandomString(16);

            this.editors.push({
                title: page.name,
                type: isNew === true ? 'new_page' : 'page',
                icon: 'th',
                pageId: page.id,
                id,
                command: page.command,
                flowId: page.flowId,
                key,
            });

            this.editors.map((editor, index) => {
                editor.index = index;
                return index;
            });

            this.setActiveEditor({ id, key });
        }
    }

    openEditor(editorDetails) {
        const isMultiTab = ALWAYS_OPEN_NEW_TAB.includes(editorDetails.name);
        const openEditor = (isMultiTab && editorDetails.key) ?
            this.editors.find(editor => editor.key === editorDetails.key) :
            this.editors.find(editor => editor.type === editorDetails.name);

        if (openEditor) {
            const { id } = openEditor;
            this.setActiveEditor({ id, key: openEditor.key });
        } else {
            const id = this.createNewEditorId(this.editors);

            // The key is used as an identifier for routing tabs
            // that can be opened multiple times, such as the players tab.
            const key = editorDetails.key || generateRandomString(16);

            this.editors.push({
                title: editorDetails.label,
                type: editorDetails.name,
                icon: editorDetails.icon,
                id,
                key,
            });

            this.editors.map((editor, index) => {
                editor.index = index;
                return index;
            });

            this.setActiveEditor({ id, key });
        }
    }

    newPageLayout = page => this.openPageLayout(page, true)

    // The action that maps to this does not seem to be used anywhere
    setEditorPageId(data) {
        const currentEditor = this.editors.filter(editor => editor.pageId === data.oldId)[0];
        currentEditor.pageId = data.newId;
    }

    setEditorName(data) {
        const editorId = parseInt(data.id, 10);
        const currentEditor = this.editors.filter(editor => editor.id === editorId)[0];
        currentEditor.title = data.name;
        currentEditor.type = data.type;
    }

    setActiveEditor({ id, key }) {
        const currentActiveEditor = this.editors.find(editor => editor.key === key);
        if (currentActiveEditor) {
            this.editors = this.editors.map((editor) => {
                editor.isActive = editor.key === key;
                return editor;
            });
        }
    }

    close({ id, flowId }) {
        const { tenantId } = store.getState().currentUser;
        const editorId = parseInt(id, 10);
        const filteredTabs = this.editors.filter(editor => editor.id !== editorId);
        const hasActiveTab = filteredTabs.filter(editor => editor.isActive).length > 0;

        if (!hasActiveTab) {

            // When the tab being closed is the current active tab
            // then we want the tab to the right of it to become active
            this.editors = filteredTabs.map((editor) => {
                if (!hasActiveTab && editor.id === editorId + 1) {
                    editor.isActive = true;
                }
                return editor;
            });
        } else {

            // If there is already an active tab thats not the
            // tab being closed then just set the tabs as
            // they are but with the closed tab filtered out
            this.editors = filteredTabs;
        }

        // This to make sure that the home tab always becomes active
        // when there are no other tabs left in the header
        if (this.editors.filter(editor => editor.isActive).length === 0) {
            this.editors[this.editors.length - 1].isActive = true;
        }

        const currentActiveEditor = this.editors.find(editor => editor.isActive);

        const { type, flowId: activeFlowId, pageId } = currentActiveEditor;
        const tabs = tabDetailsObjects;
        const slug = type === 'flow' || type === 'page' ? type : Object.values(tabs).find(t => t.name === type).slug;
        const route = generateRouteUrl({
            tabType: slug,
            tenantId,
            options: {
                tabKey: currentActiveEditor.key,
                pageId,
                flowId: activeFlowId,
            },
        });
        navigate(route);

        if (flowId) {
            store.dispatch(
                closeFlow(flowId),
            );
        }
    }

    closeAll(isHomeActive) {
        this.editors = [{ type: 'flows', id: 0, isActive: isHomeActive, title: 'Flows', key: homeKey }];
    }

    closePage(id) {
        this.close(this.editors.filter(editor => editor.pageId === id)[0]);
    }

    openTenantSelector() {
        this.isTenantSelectorOpen = true;
    }

    closeTenantSelector() {
        this.isTenantSelectorOpen = false;
    }
}

export default alt.createStore(EditorStore, 'editor');
